import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { money, fmtDate, todayISO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { downloadSalesReport } from "@/lib/salesReportPdf";
import { PageHeader, StatusBadge, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function rangeFor(mode, day, month, from, to) {
  if (mode === "dia") return { start: day, end: day, label: `Día ${fmtDate(day)}` };
  if (mode === "semana") {
    const d = new Date(`${day}T12:00:00`);
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { start: iso(mon), end: iso(sun), label: `Semana del ${fmtDate(iso(mon))} al ${fmtDate(iso(sun))}` };
  }
  if (mode === "mes") {
    const [y, m] = month.split("-").map(Number);
    return { start: `${month}-01`, end: iso(new Date(y, m, 0)), label: `Mes de ${MONTHS[m - 1]} ${y}` };
  }
  return { start: from, end: to, label: `Del ${fmtDate(from)} al ${fmtDate(to)}` };
}

const MODES = [{ v: "dia", l: "Diario" }, { v: "semana", l: "Semanal" }, { v: "mes", l: "Mensual" }, { v: "rango", l: "Personalizado" }];

export default function Reports() {
  const { settings } = useApp();
  const [params] = useSearchParams();
  const today = todayISO();
  const [mode, setMode] = useState("mes");
  const [day, setDay] = useState(today);
  const [month, setMonth] = useState(params.get("mes") || today.slice(0, 7));
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const sym = settings?.currency_symbol || "$";

  useEffect(() => {
    const p = params.get("mes");
    if (p) {
      setMode("mes");
      setMonth(p);
    }
  }, [params]);

  const range = useMemo(() => rangeFor(mode, day, month, from, to), [mode, day, month, from, to]);

  useEffect(() => {
    if (!range.start || !range.end || range.start > range.end) {
      setReport(null);
      return;
    }
    api.get("/reports/sales", { params: { start: range.start, end: range.end } }).then((r) => setReport(r.data));
  }, [range.start, range.end]);

  const download = async () => {
    setBusy(true);
    try {
      await downloadSalesReport({ report, settings, periodLabel: range.label, filename: `historial-ventas_${range.start}_${range.end}.pdf` });
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally {
      setBusy(false);
    }
  };

  const s = report?.summary;
  return (
    <div>
      <PageHeader title="Historial de ventas" subtitle={range.label}>
        <Button onClick={download} disabled={!report || !settings || busy} className="bg-amber-700 hover:bg-amber-800" data-testid="report-download-pdf">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Descargar PDF
        </Button>
      </PageHeader>
      <div className="rounded-xl border bg-white p-5 mb-6 flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="flex flex-wrap gap-2">
          {MODES.map((x) => (
            <button key={x.v} onClick={() => setMode(x.v)} data-testid={`report-mode-${x.v}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${mode === x.v ? "bg-[#1E1B18] text-amber-400 border-[#1E1B18]" : "bg-white hover:bg-stone-100"}`}>
              {x.l}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          {(mode === "dia" || mode === "semana") && <Field label={mode === "dia" ? "Día" : "Cualquier día de la semana"}><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="report-day-input" /></Field>}
          {mode === "mes" && <Field label="Mes"><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-input" /></Field>}
          {mode === "rango" && (
            <>
              <Field label="Desde"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-input" /></Field>
              <Field label="Hasta"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-input" /></Field>
            </>
          )}
        </div>
      </div>
      {range.start > range.end && <p className="mb-4 text-sm text-red-600" data-testid="report-range-error">La fecha "Desde" debe ser anterior a "Hasta".</p>}
      {s && (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
          {[["Total vendido", money(s.total, sym), "report-total"], ["Notas", `${s.count}${s.cancelled ? ` (+${s.cancelled} canceladas)` : ""}`, "report-count"],
            ["Cobrado (pagadas)", money(s.paid, sym), "report-paid"], ["Saldo por cobrar", money(s.pending, sym), "report-pending"]].map(([l, v, id]) => (
            <div key={id} className="rounded-xl border bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{l}</p>
              <p className="mt-2 text-2xl font-mono font-bold" data-testid={id}>{v}</p>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl border bg-white overflow-x-auto" data-testid="report-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-4 py-3">Folio</th><th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Conceptos</th>
              <th className="px-4 py-3">Estado</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {report?.sales.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-left sm:text-center text-stone-400">No hay ventas en este periodo</td></tr>}
            {report?.sales.map((v) => (
              <tr key={v.id} className={v.status === "cancelada" ? "opacity-50" : ""} data-testid={`report-row-${v.folio}`}>
                <td className="px-4 py-3 font-mono font-semibold text-amber-800">{v.folio}</td>
                <td className="px-4 py-3">{fmtDate(v.date)}</td>
                <td className="px-4 py-3 font-medium">{v.client_name}</td>
                <td className="px-4 py-3 text-xs text-stone-600 max-w-xs">{v.items.map((i) => `${i.quantity}× ${i.description}`).join(", ")}</td>
                <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                <td className="px-4 py-3 text-right font-mono">{money(v.total, sym)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(v.balance, sym)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
